import { type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { List as AntdList, type ListProps as AntdListProps } from "antd";
import { createStyles } from "antd-style";

import { assertPaginationControlled } from "./shared/assertPaginationControlled";

import type { AvE2EProps } from "../types";
import type {
  PaginationConfig as AntdPaginationConfig,
  PaginationProps as AntdPaginationProps,
} from "antd/es/pagination";

type PaginationItemType = "page" | "prev" | "next" | "jump-prev" | "jump-next";

type ListPaginationConfig = Omit<AntdPaginationConfig, "itemRender"> & {
  /**
   * @deprecated This prop is ignored and controlled manually in the central component
   */
  itemRender?: AntdPaginationProps["itemRender"];
  /**
   * Gives each page number a real `href`, so crawlers can follow it and mouse users can open it in a
   * new tab (modifier or middle click, or the context menu). A plain click still changes page through
   * `onChange`. The link is kept out of the tab order, since rc-pagination already makes the page item
   * focusable; keyboard users change page with Enter on that item.
   */
  pageHref?: (page: number) => string | undefined;
};

interface StyledListProps<T> extends AvE2EProps, Omit<AntdListProps<T>, "children" | "pagination"> {
  unstyled?: boolean;
  pagination?: ListPaginationConfig | false;
}

const useStyles = createStyles(({ css }) => ({
  unstyledList: css`
    li.ant-list-item {
      padding: 0;
    }
  `,
}));

function keepPlainClickInPagination(event: MouseEvent<HTMLAnchorElement>) {
  const opensElsewhere = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

  if (opensElsewhere) {
    event.stopPropagation();
  } else {
    event.preventDefault();
  }
}

function leaveEnterToTheLink(event: KeyboardEvent<HTMLAnchorElement>) {
  if (event.key === "Enter") {
    event.stopPropagation();
  }
}

function CustomList<T>({
  testIdPrefix,
  unstyled,
  className,
  pagination,
  ...props
}: StyledListProps<T>) {
  assertPaginationControlled(pagination, "<List>");

  const { styles } = useStyles();
  const paginationTestId = `${testIdPrefix}-list-pagination`;

  const { pageHref, ...antdPagination } = pagination || {};
  const finalPagination = pagination ? antdPagination : false;

  if (finalPagination) {
    //@ts-expect-error this prop works
    finalPagination["data-testid"] = paginationTestId;

    finalPagination.itemRender = (page: number, type: PaginationItemType, element: ReactNode) => {
      switch (type) {
        case "page": {
          const href = pageHref?.(page);

          return (
            <span data-testid={`${paginationTestId}-page-${page}`}>
              {href ? (
                <a
                  href={href}
                  tabIndex={-1}
                  onClick={keepPlainClickInPagination}
                  onKeyDown={leaveEnterToTheLink}
                >
                  {page}
                </a>
              ) : (
                element
              )}
            </span>
          );
        }

        default:
          return <span data-testid={`${paginationTestId}-${type}`}>{element}</span>;
      }
    };
  }

  return (
    <AntdList
      className={unstyled ? styles.unstyledList : className}
      data-testid={`${testIdPrefix}-list`}
      {...props}
      pagination={finalPagination}
    />
  );
}

export const List = Object.assign(CustomList, {
  Item: AntdList.Item,
});
